// Manual slip-verification action modals, shared by PaymentDetailPage,
// PaymentListPage and the parking sessions page. EP-161 (D3): the ML reading
// decides nothing for parking either, so a person verifies every parking slip
// here; the optional `reference` shows that reading beside the decision.
//
// Each modal owns its reason state, mutation and success toast; callers only
// control visibility via { paymentId, isOpen, onClose }. The usePayments hooks
// already invalidate both the list and detail query trees.
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Modal } from '@/components/ui'
import { Textarea } from '@/components/ui/Textarea'
import {
  useVerifyPayment,
  useRejectPayment,
  useRetryVerification,
  useFlagFraud,
} from '@/hooks/usePayments'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { SlipReferenceBlock, type SlipReference } from '@/components/payments/SlipReferenceBlock'

interface VerificationActionModalProps {
  paymentId: number
  isOpen: boolean
  onClose: () => void
}

interface DecisionModalProps extends VerificationActionModalProps {
  // EP-161: the ML reading, rendered above the reason for reference only.
  reference?: SlipReference
  // Called after the decision saved, before the modal closes (the sessions
  // page refreshes its rows; the payment queries are refreshed by the hook).
  onSuccess?: () => void
}

interface VerifyPaymentModalProps extends DecisionModalProps {
  // Prefilled audit reason (e.g. the field-by-field manual slip check summary
  // from SlipFieldChecklist). Re-seeded each time the modal opens; the
  // operator can still edit it before submitting.
  initialReason?: string
}

interface RejectPaymentModalProps extends DecisionModalProps {
  // EP-161 §10 item 17: a parking payment's reject reason is shown to the
  // visitor on the kiosk pay page, so the modal says so.
  parking?: boolean
}

export function VerifyPaymentModal({
  paymentId,
  isOpen,
  onClose,
  initialReason,
  reference,
  onSuccess,
}: VerifyPaymentModalProps) {
  const { t } = useTranslation('payments')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const verifyPayment = useVerifyPayment()
  const [reason, setReason] = useState('')

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset the reason field to its initial value each time the modal (re)opens
    if (isOpen) setReason(initialReason ?? '')
  }, [isOpen, initialReason])

  const close = () => {
    setReason('')
    onClose()
  }

  const handleVerify = async () => {
    await verifyPayment.mutateAsync({ id: paymentId, reason: reason || undefined })
    addToast({ type: 'success', message: t('verification.statuses.verified') })
    onSuccess?.()
    close()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('actions.verify')}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleVerify}
            loading={verifyPayment.isPending}
            variant="success"
          >
            {t('actions.verify')}
          </Button>
        </>
      }
    >
      {reference && (
        <div className="mb-4">
          <SlipReferenceBlock {...reference} />
        </div>
      )}
      <p className="text-sm text-base-content/70 mb-4">{t('verification.verify_confirm')}</p>
      <Textarea
        label={t('verification.verify_reason_label')}
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  )
}

export function RejectPaymentModal({
  paymentId,
  isOpen,
  onClose,
  reference,
  onSuccess,
  parking = false,
}: RejectPaymentModalProps) {
  const { t } = useTranslation('payments')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const rejectPayment = useRejectPayment()
  const [reason, setReason] = useState('')

  const close = () => {
    setReason('')
    onClose()
  }

  const handleReject = async () => {
    if (!reason.trim()) return
    await rejectPayment.mutateAsync({ id: paymentId, reason })
    addToast({ type: 'success', message: t('verification.statuses.rejected') })
    onSuccess?.()
    close()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('actions.reject')}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {tc('actions.cancel')}
          </Button>
          <Button
            variant="error"
            onClick={handleReject}
            loading={rejectPayment.isPending}
            disabled={!reason.trim()}
          >
            {t('actions.reject')}
          </Button>
        </>
      }
    >
      {reference && (
        <div className="mb-4">
          <SlipReferenceBlock {...reference} />
        </div>
      )}
      <Textarea
        label={t('verification.reject_reason')}
        rows={4}
        placeholder={t('verification.reject_reason_placeholder')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        required
      />
      {parking && (
        <p className="mt-2 text-sm text-base-content/70">{t('verification.reject_reason_visible_to_visitor')}</p>
      )}
    </Modal>
  )
}

export function RetryVerificationModal({ paymentId, isOpen, onClose }: VerificationActionModalProps) {
  const { t } = useTranslation('payments')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const retryVerification = useRetryVerification()

  const handleRetry = async () => {
    await retryVerification.mutateAsync(paymentId)
    addToast({ type: 'success', message: t('verification.statuses.pending') })
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('actions.retry_verification')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button onClick={handleRetry} loading={retryVerification.isPending}>
            {t('actions.retry_verification')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-base-content/70">{t('verification.retry_confirm')}</p>
    </Modal>
  )
}

// EP-143 T143-013 — Confirm-only modal: no free-text reason, just a destructive
// confirm. Staff+ only (gate enforced at the list/detail call sites via the
// `canFlagFraud` check — this modal does not self-gate on role).
export function FlagFraudModal({ paymentId, isOpen, onClose }: VerificationActionModalProps) {
  const { t } = useTranslation('payments')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const flagFraud = useFlagFraud()

  const handleFlag = async () => {
    await flagFraud.mutateAsync(paymentId)
    addToast({ type: 'success', message: t('fraud.flagged_success') })
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('fraud.confirm_title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button variant="error" onClick={handleFlag} loading={flagFraud.isPending}>
            {t('actions.flag_fraud')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-base-content/70">{t('fraud.confirm_body')}</p>
    </Modal>
  )
}
